import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Typography,
  Divider,
  Skeleton,
  Box
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "../Utility/UserMessageContext";
import Grid2 from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded"; // The '+' icon
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { fetchBillingDetails } from "../API/BillingApi";
import AuthContext from "../Context/AuthProvider";
import { removeBill } from "../API/BillingApi";
const BillList = ({ onNewBill, onEditDetails, onDelete }) => {
  const { auth } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
   const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if screen is small (mobile)

  const loadBillingData = async () => {
    setLoading(true);
    const facilityId = auth.selectedFacility.id;

    const today = new Date().toISOString().split("T")[0];
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 20);
    const from = fromDate.toISOString().split("T")[0];

    const response = await fetchBillingDetails(auth, facilityId, from, today);

    if (response.isSuccess && Array.isArray(response.data)) {
      setRows(
        response.data.map((bill) => ({
          id: bill.id || bill.billNo,
          billNo: bill.displayId || "N/A",
          visitId: bill.visit.displayId || "N/A",
          patientName:
            `${bill.visit?.patientFirstName || ""} ${
              bill.visit?.patientLastName || ""
            }`.trim() || "N/A",
          billStatus: bill.status || "N/A",
          generatedBy: bill.createdByUserName || "N/A",
          createdDate: bill.billDate
            ? new Date(bill.billDate)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                .replace(/,/g, "")
                .toUpperCase()
            : "N/A",
          totalAmount: bill.totalAmount ?? 0,
          totalPayment: bill.paidAmount ?? 0,
          totalBalance: bill.balanceAmount ?? 0,
        }))
      );
    } else {
      console.error("Failed to fetch billing data:", response.data);
      setRows([]);
    }

    setLoading(false);
  };
  const handleDeleteBill = async (billId) => {
  const confirm = window.confirm("Are you sure you want to delete this bill?");
  if (!confirm) return;

  const response = await removeBill(auth, billId);
  if (response.isSuccess) {
    showSnackbar("✅ Bill deleted successfully!", "success");
    loadBillingData(); // Refresh the list
  } else {
    showSnackbar(response.data || "❌ Failed to delete bill", "error");
  }
};
  useEffect(() => {
    loadBillingData();
  }, [auth.selectedFacility?.id]);

  const columns = [
    { field: "billNo", headerName: "Bill", width: 200 },  
    { field: "patientName", headerName: "Patient Name", width: 200 },
    { field: "billStatus", headerName: "Bill Status", width: 150 },
    { field: "generatedBy", headerName: "Generated By", width: 200 },
    { field: "createdDate", headerName: "Created Date", width: 150 },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      width: 120,
      renderCell: (params) => {
        const amount = Number(params.row.totalAmount || 0);
        return `Rs ${amount.toFixed(2)}`;
      },
    },
    {
      field: "totalPayment",
      headerName: "Total Payment",
      width: 120,
      renderCell: (params) => {
        const row = params.row;
        if (row?.id === "TOTAL") {
          return <strong>Rs {row.totalPayment}</strong>;
        }
        const payment = Number(row.totalPayment || 0);
        return `Rs ${payment.toFixed(2)}`;
      },
    },
    {
      field: "totalBalance",
      headerName: "Total Balance",
      width: 120,
      renderCell: (params) => {
        const row = params.row;
        if (row?.id === "TOTAL") {
          return <strong>Rs {row.totalBalance}</strong>;
        }
        const balance = Number(row.totalBalance || 0);
        return `Rs ${balance.toFixed(2)}`;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit Details" arrow>
            <IconButton onClick={() => onEditDetails(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Remove Row" arrow>
            <IconButton onClick={() => handleDeleteBill(params.row.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const renderBillCards = () => {
    if (loading) {
      return (
        <Box sx={{ p: 1 }}>
          {Array.from(new Array(5)).map((_, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={25} />
                <Divider sx={{ my: 1 }} />
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="90%" height={20} />
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                <Skeleton variant="rectangular" width={80} height={30} sx={{ mr: 1 }} />
                <Skeleton variant="rectangular" width={80} height={30} />
              </CardActions>
            </Card>
          ))}
        </Box>
      );
    }

    if (!loading && rows.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No recent bills to display.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 1, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        {rows.map((bill) => (
          <Card key={bill.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">
                Bill: {bill.billNo}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" gutterBottom>
                Patient: {bill.patientName}
              </Typography>
              <Typography variant="body2">Status: {bill.billStatus}</Typography>
              <Typography variant="body2">
                Generated By: {bill.generatedBy}
              </Typography>
              <Typography variant="body2">
                Created Date: {bill.createdDate}
              </Typography>
              <Typography variant="body2">
                Total Amount: Rs {Number(bill.totalAmount).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                Total Payment: Rs {Number(bill.totalPayment).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                Total Balance: Rs {Number(bill.totalBalance).toFixed(2)}
              </Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEditDetails(bill)}
              >
                
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon />}
              onClick={() => handleDeleteBill(bill.id)}
              >
                
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <Grid2 container spacing={2} sx={{ p: 1 }} size={12}>
      {/* New Bill Button */}
      <Grid2 size={12}>
        <Tooltip title="New Bill" arrow>
          <Button
            variant="contained"
            color="secondary"
            onClick={onNewBill}
            // Conditional styling and content for mobile
            size={isMobile ? "small" : "medium"} // Changed to "small" for mobile
            sx={{
              ...(isMobile && {
                minWidth: 'unset', // Remove default minimum width
                width: '40px',     // Smaller width for mobile button
                height: '40px',    // Smaller height for mobile button
                borderRadius: '50%', // Keep it circular
                padding: 0,        // Remove default padding to center icon
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }),
            }}
          >
            {/* Render only the icon on mobile, icon + text on desktop */}
            {isMobile ? (
              <AddRoundedIcon fontSize="medium" /> // Adjusted icon size for smaller button
            ) : (
              <>
                <AddRoundedIcon fontSize="small" />
                New Bill
              </>
            )}
          </Button>
        </Tooltip>
      </Grid2>

      {/* Conditional Rendering: DataGrid for desktop, Cards for mobile */}
      <Grid2 item size={12} sx={{  maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
        {isMobile ? (
          renderBillCards()
        ) : (
          <DataGrid
           // Adjusted row height for better visibility
            rows={rows}
            columns={columns}
            pageSizeOptions={[5, 10, 20]}
            autoHeight
            loading={loading}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            sx={{ width: "100%" }}
          />
        )}
      </Grid2>
    </Grid2>
  );
};

export default BillList;